import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AppComponent } from './app.component';
import { ClienteComponent } from './pages/cliente/cliente.component';
import { VendedorComponent } from './pages/vendedor/vendedor.component';
import { UsuarioComponent } from './pages/usuario/usuario.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProdutoComponent } from './pages/produto/produto.component';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { MarcaComponent } from './pages/marca/marca.component';
import { FornecedorComponent } from './pages/./fornecedor/fornecedor.component';
import { EstoqueHistoricoComponent } from './pages/./estoque-historico/estoque-historico.component';
import { DashboardComponent } from './pages/./dashboard/dashboard.component';
import { MenuComponent } from './components/menu/menu.component';
import { RouterModule, Routes } from '@angular/router';
import { VendaComponent } from './pages/venda/venda.component';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { InputNumberModule } from 'primeng/inputnumber';
import { PagamentoModalComponent } from './pages/venda/pagamento-modal/pagamento-modal.component';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputMaskModule } from 'primeng/inputmask';
import { EstoqueComponent } from './pages/estoque/estoque.component';
import { ListaVendasComponent } from './pages/lista-vendas/lista-vendas.component';
import { ListaVendasPorDatasComponent } from './pages/lista-vendas-por-datas/lista-vendas-por-datas.component';
import { LoginComponent } from './pages/login/login.component';
import { EmpresaComponent } from './pages/empresa/empresa.component';
import { EmpresaFormComponent } from './pages/empresa/form/empresa-form.component';
import { AssinaturaListComponent } from './pages/assinatura/list/assinatura-list.component';
import { AssinaturaDetailComponent } from './pages/assinatura/detail/assinatura-detail.component';
import { AssinaturaFormComponent } from './pages/assinatura/form/assinatura-form.component';
import { ForbiddenComponent } from './pages/forbidden/forbidden.component';
import { LivroCaixaComponent } from './pages/caixa-por-datas/livro-caixa.component';
import { BuscarVendaComponent } from './pages/buscar-venda/buscar-venda.component';

// JWT e Authentication
import { JwtInterceptor } from './core/interceptors/jwt.interceptor';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { AuthGuard } from './core/guards/auth.guard';
import { TenantInterceptor } from './core/interceptors/tenant.interceptor';
import { RoleGuard } from './core/guards/role.guard';

// Importando a configuração regional do Brasil (pt-BR)
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { LOCALE_ID } from '@angular/core';

registerLocaleData(localePt);

const routes: Routes = [
  { path: 'login', component: LoginComponent },
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
  { path: 'dashboard', component: DashboardComponent, canActivate: [AuthGuard] },
  { path: 'produtos', component: ProdutoComponent, canActivate: [AuthGuard] },
  { path: 'clientes', component: ClienteComponent, canActivate: [AuthGuard] },
  { path: 'vendedores', component: VendedorComponent, canActivate: [AuthGuard] },
  { path: 'usuarios', component: UsuarioComponent, canActivate: [AuthGuard] },
  { path: 'marcas', component: MarcaComponent, canActivate: [AuthGuard] },
  { path: 'fornecedores', component: FornecedorComponent, canActivate: [AuthGuard] },
  { path: 'estoque', component: EstoqueComponent, canActivate: [AuthGuard] },
  { path: 'historico', component: EstoqueHistoricoComponent, canActivate: [AuthGuard] },
  { path: 'venda', component: VendaComponent, canActivate: [AuthGuard] },
  { path: 'vendas', component: ListaVendasComponent, canActivate: [AuthGuard] },
  { path: 'vendas/por-data', component: ListaVendasPorDatasComponent, canActivate: [AuthGuard] },
  { path: 'empresa', component: EmpresaComponent, canActivate: [AuthGuard] },
  { path: 'caixa', component: LivroCaixaComponent, canActivate: [AuthGuard] },
  { path: 'buscar-venda', component: BuscarVendaComponent, canActivate: [AuthGuard] },
  // Rotas de Empresa (protegidas por role MASTER)
  { path: 'empresas', component: EmpresaFormComponent, canActivate: [AuthGuard, RoleGuard], data: { role: 'MASTER' } },
  { path: 'empresas/cadastrar', redirectTo: 'empresas', pathMatch: 'full' },
  { path: 'empresas/:id/editar', component: EmpresaFormComponent, canActivate: [AuthGuard, RoleGuard], data: { role: 'MASTER' } },
  // Assinaturas (protegidas por role MASTER)
  { path: 'assinaturas', component: AssinaturaListComponent, canActivate: [AuthGuard, RoleGuard], data: { role: 'MASTER' } },
  { path: 'assinaturas/cadastrar', component: AssinaturaFormComponent, canActivate: [AuthGuard, RoleGuard], data: { role: 'MASTER' } },
  { path: 'assinaturas/cliente/:clienteId', component: AssinaturaListComponent, canActivate: [AuthGuard, RoleGuard], data: { role: 'MASTER' } },
  { path: 'assinaturas/:id', component: AssinaturaDetailComponent, canActivate: [AuthGuard, RoleGuard], data: { role: 'MASTER' } },
  { path: 'assinaturas/:id/editar', component: AssinaturaFormComponent, canActivate: [AuthGuard, RoleGuard], data: { role: 'MASTER' } },
  { path: 'forbidden', component: ForbiddenComponent },
  { path: '**', redirectTo: '/login' },
];

@NgModule({
  declarations: [
    AppComponent,
    ClienteComponent,
    VendedorComponent,
    UsuarioComponent,
    ProdutoComponent,
    MarcaComponent,
    FornecedorComponent,
    EstoqueHistoricoComponent,
    DashboardComponent,
    MenuComponent,
    VendaComponent,
    PagamentoModalComponent,
    EstoqueComponent,
    ListaVendasComponent,
    ListaVendasPorDatasComponent,
    LoginComponent,
    EmpresaComponent,
    EmpresaFormComponent,
    AssinaturaListComponent,
    AssinaturaDetailComponent,
    AssinaturaFormComponent,
    ForbiddenComponent,
    LivroCaixaComponent,
    BuscarVendaComponent,
  ],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    NgxMaskDirective,
    NgxMaskPipe,
    BrowserAnimationsModule,
    InputTextModule,
    ButtonModule,
    TableModule,
    DropdownModule,
    AutoCompleteModule,
    InputNumberModule,
    DialogModule,
    ToastModule,
    ConfirmDialogModule,
    InputMaskModule,
    RouterModule.forRoot(routes),
  ],
  providers: [
    provideNgxMask(),
    MessageService,
    ConfirmationService,
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TenantInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
